/* eslint react-hooks/exhaustive-deps: 0 */ // --> OFF
/* eslint no-nested-ternary: 0 */ // --> OFF
import Bole from '@haptik/helpers/build/esm/bole';
import React, { useReducer, useLayoutEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import Box from 'relements/build/esm/ui/box';
import Text from 'relements/build/esm/ui/text';
import Dialog from 'relements/build/esm/overlays/dialog';

import hashHistory from '@src/History';
import { AGENT_ESCALATION_ROUTE } from '@src/constants/Routes';
import { getURLParams } from '@src/utils/fp';

import Home from './components/Home';
import Setup from './components/Setup';
import HomeLoading from './components/Home/HomeLoading';
import SetupLoading from './components/Setup/SetupLoading';
import HelpSection from './components/HelpSection';
import IframeOverlay from './components/IframeOverlay';
import VideoPlayer from './components/VideoPlayer';

import reducer, { initialState, init } from './store/reducer';
import {
  fetchECT,
  fetchECTs,
  createECT,
  updateECT,
  deleteECT,
  activateInvalidBotDialogue,
  deactivateInvalidBotDialogue,
  activateContactSalesTeamDialog,
  deactivateContactSalesTeamDialog,
} from './store/actions';

const AgentEscalation = () => {
  const [state, dispatch] = useReducer(reducer, initialState, init);
  const {
    isInvalidBotDialogueActive,
    isActiveToolConfigured,
    isActiveToolExist,
    contactSalesTeamDialogToolName,
    isContactSalesTeamDialogActive,
    activeToolData,
    toolList,
    fields,
    videoID,
    contactUsDocUrl,
    ectsFetched,
    ectCredentials,
    loading,
  } = state;

  /* 
   fqdn: fully qualified domain name.
   It uniquely identifies data for an ECT tool in the BE.
   */
  const { fqdn, botId, clientId, businessId: businessIdStr } = useParams();
  const businessId = Number(businessIdStr);

  const saveConfiguration = (
    credentials,
    prevCredentials,
    callbackURL,
    onFail
  ) => {
    if (isActiveToolExist && isActiveToolConfigured)
      updateECT(
        clientId,
        businessId,
        fqdn,
        credentials,
        prevCredentials,
        onFail
      )(dispatch);
    else
      createECT(
        clientId,
        businessId,
        fqdn,
        credentials,
        prevCredentials,
        callbackURL,
        onFail
      )(dispatch);
  };

  const navToHome = useCallback(() => {
    hashHistory.push(`${AGENT_ESCALATION_ROUTE(botId, clientId, businessId)}`);
  }, [botId, clientId, businessId]);

  const navToSetup = useCallback(() => {
    hashHistory.push(
      `${AGENT_ESCALATION_ROUTE(botId, clientId, businessId)}/${
        activeToolData?.fqdn
      }`
    );
  }, [botId, clientId, businessId, activeToolData?.fqdn]);

  const navToSetupFqdn = useCallback(
    (fqdn) => {
      hashHistory.push(
        `${AGENT_ESCALATION_ROUTE(botId, clientId, businessId)}/${fqdn}`
      );
    },
    [botId, clientId, businessId]
  );

  const navToBM = useCallback(() => {
    window.location = `${window.location.origin}/mogambo/#/business-manager/${businessId}/`;
    // TODO: Find better alternative
  }, [businessId]);

  const navToAthena = useCallback(() => {
    window.open(`${window.location.origin}/athena`, '_blank', 'noopener');
  }, []);

  const showContactSalesTeamDialog = useCallback((toolName) => {
    dispatch(activateContactSalesTeamDialog(toolName));
  }, []);

  const navToContactSupport = useCallback(() => {
    window.open(contactUsDocUrl, '_blank', 'noopener');
  }, []);

  const ectToolName = ectCredentials?.[fqdn]?.name;

  const description = ectToolName
    ? `Don’t want to set up ${ectToolName} by yourself? No worries, you can assign it to your colleague.`
    : "Don't want to set it up by yourself? No worries, you can ask your colleague for help.";

  useLayoutEffect(() => {
    if (clientId !== 'null' && !Number.isNaN(businessId)) {
      if (!ectsFetched) {
        fetchECTs(clientId, fqdn)(dispatch);
      } else if (fqdn) {
        if (ectCredentials[fqdn]) {
          fetchECT(clientId, fqdn)(dispatch);
        } else {
          navToHome();
          const tool = toolList.find((tool) => tool.fqdn === fqdn);
          const toolName = tool?.name ?? fqdn;
          showContactSalesTeamDialog(toolName);
        }
      }
    } else {
      dispatch(activateInvalidBotDialogue);
    }
  }, [fqdn, navToHome, clientId, businessId]);

  useLayoutEffect(() => {
    if (activeToolData.fqdn && fqdn && fqdn !== activeToolData.fqdn) {
      navToHome();
      Bole.alert(
        `Redirected to Home Page, as you can setup only one ECT at a time`
      );
    }
  }, [activeToolData, navToHome, fqdn]);

  const deactivateConfiguration = (onFail, onSuccess) => {
    deleteECT(clientId, activeToolData.fqdn, onFail, onSuccess)(dispatch);
  };

  return (
    <Box padding="zero" backgroundColor="grey.soft" height="100%">
      {fqdn ? (
        loading ? (
          <SetupLoading />
        ) : (
          <Setup
            isActiveToolExist={isActiveToolExist}
            toolName={ectCredentials?.[fqdn]?.name}
            iconUrl={ectCredentials?.[fqdn]?.iconUrl}
            callbackURL={ectCredentials?.[fqdn]?.callbackURL}
            callbackUrlDomain={ectCredentials?.[fqdn]?.callbackUrlDomain}
            fields={fields}
            navToHome={navToHome}
            saveConfiguration={saveConfiguration}
            deactivateConfiguration={deactivateConfiguration}
            zohoAuthResponse={ectCredentials?.[fqdn]?.zohoAuthResponse}
            fqdn={fqdn}
          />
        )
      ) : loading ? (
        <HomeLoading />
      ) : (
        <Home
          isActiveToolExist={isActiveToolExist}
          activeToolData={activeToolData}
          toolList={toolList}
          contactUsDocUrl={contactUsDocUrl}
          deactivateConfiguration={deactivateConfiguration}
          ectCredentials={ectCredentials}
          navToBM={navToBM}
          navToSetup={navToSetup}
          navToSetupFqdn={navToSetupFqdn}
          navToAthena={navToAthena}
          activateContactSalesTeamDialog={showContactSalesTeamDialog}
        />
      )}
      {!isActiveToolExist && !loading && (
        <HelpSection description={description}>
          {!fqdn ? (
            videoID &&
            false && (
              <>
                <div style={{ borderTop: '1px solid #D5D5D5' }} />
                <Box padding="16px 21px" flexDirection="column">
                  <Text variants="body.md" color="black.haptik">
                    Watch this quick video to know more
                  </Text>
                  <VideoPlayer videoID={videoID} />
                </Box>
              </>
            )
          ) : (
            <IframeOverlay
              src={ectCredentials?.[fqdn]?.toolDocUrl ?? contactUsDocUrl}
            />
          )}
        </HelpSection>
      )}
      <Dialog
        onClose={() => dispatch(deactivateInvalidBotDialogue)}
        active={isInvalidBotDialogueActive}
        title="Bot is not linked to a business"
        subtitle="Please select a bot that is linked to a business from the topbar."
        primaryAction={() => dispatch(deactivateInvalidBotDialogue)}
        primaryActionText="OK"
        secondaryActionText="Cancel"
      />
      <Dialog
        onClose={() => dispatch(deactivateContactSalesTeamDialog)}
        active={isContactSalesTeamDialogActive}
        title={`To configure ${contactSalesTeamDialogToolName}`}
        subtitle={`Please reach out to our support team by clicking the button below and we will get back to you within 48 hours to help you configure ${contactSalesTeamDialogToolName}`}
        primaryAction={navToContactSupport}
        primaryActionText="Contact Us"
        secondaryActionText="Cancel"
      />
    </Box>
  );
};

export default AgentEscalation;
